import { Component } from "@/types";
import type { BaseChartProperties } from "@/types/chart-properties";
import { ChartMixinBase, MixinResult } from "./ChartMixinBase";

/**
 * Mixin for handling animation-related commands across all chart types
 */
export class AnimationMixin extends ChartMixinBase {
  parseCommand(command: string, component: Component): MixinResult {
    const lowerCommand = command.toLowerCase();
    const changes: Partial<BaseChartProperties> = {};

    // Enable/disable animations
    if (this.hasKeywords(command, ['enable animation', 'add animation', 'animate', 'with animation'])) {
      changes.animation = true;
    }
    if (this.hasKeywords(command, ['disable animation', 'remove animation', 'no animation', 'static'])) {
      changes.animation = false;
    }

    // Animation duration
    const durationMatch = command.match(/animation\s+(?:duration|speed|time)\s+(?:of\s+)?(\d+(?:\.\d+)?)\s*(?:ms|milliseconds|s|seconds)?/i);
    if (durationMatch) {
      let duration = parseFloat(durationMatch[1]);
      // Convert seconds to milliseconds if needed
      if (command.includes('s') && !command.includes('ms')) {
        duration *= 1000;
      }
      changes.animationDuration = duration;
    }

    // Animation speed presets
    if (this.hasKeywords(command, ['fast animation', 'quick animation'])) {
      changes.animationDuration = 300;
    }
    if (this.hasKeywords(command, ['slow animation', 'smooth animation'])) {
      changes.animationDuration = 1500;
    }
    if (this.hasKeywords(command, ['normal animation', 'default animation'])) {
      changes.animationDuration = 800;
    }

    // Animation easing
    const easingType = this.parseEasing(command);
    if (easingType) {
      changes.animationEasing = easingType;
    }

    // Animation type
    const animationType = this.parseAnimationType(command);
    if (animationType) {
      changes.animationType = animationType;
    }

    // Animation begin time (delay)
    const delayMatch = command.match(/(?:delay|start after|begin after)\s+(\d+(?:\.\d+)?)\s*(?:ms|milliseconds|s|seconds)?/i);
    if (delayMatch) {
      let delay = parseFloat(delayMatch[1]);
      if (command.includes('s') && !command.includes('ms')) {
        delay *= 1000;
      }
      changes.animationBegin = delay;
    }

    // Stagger animation for multiple elements
    if (this.hasKeywords(command, ['stagger', 'cascade', 'sequential', 'one by one'])) {
      changes.animationStagger = true;

      // Stagger delay
      const staggerMatch = command.match(/stagger\s+(?:by\s+)?(\d+(?:\.\d+)?)\s*(?:ms|milliseconds)?/i);
      if (staggerMatch) {
        changes.animationStaggerDelay = parseFloat(staggerMatch[1]);
      } else {
        changes.animationStaggerDelay = 50; // Default stagger delay
      }
    }

    // Loop animation
    if (this.hasKeywords(command, ['loop', 'repeat', 'continuous'])) {
      changes.animationLoop = true;

      // Number of loops
      const loopMatch = command.match(/(?:loop|repeat)\s+(\d+)\s+times?/i);
      if (loopMatch) {
        changes.animationLoopCount = parseInt(loopMatch[1]);
      }
    }

    // Hover animations
    if (this.hasKeywords(command, ['hover animation', 'on hover', 'mouse over'])) {
      changes.hoverAnimation = true;

      if (command.includes('disable') || command.includes('no')) {
        changes.hoverAnimation = false;
      }
    }

    // Entry animation
    if (this.hasKeywords(command, ['entry animation', 'entrance', 'fade in', 'slide in'])) {
      if (command.includes('fade')) {
        changes.entryAnimation = 'fade';
      } else if (command.includes('slide')) {
        changes.entryAnimation = 'slide';
      } else if (command.includes('scale') || command.includes('grow')) {
        changes.entryAnimation = 'scale';
      } else {
        changes.entryAnimation = 'default';
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private parseEasing(command: string): string | null {
    const easingTypes: Record<string, string> = {
      'linear': 'linear',
      'ease in': 'ease-in',
      'ease out': 'ease-out',
      'ease in out': 'ease-in-out',
      'bounce': 'bounce',
      'elastic': 'elastic',
      'back': 'back',
      'exponential': 'expo',
      'circular': 'circ',
      'sine': 'sine',
      'quad': 'quad',
      'cubic': 'cubic',
      'quart': 'quart',
      'quint': 'quint'
    };

    for (const [key, value] of Object.entries(easingTypes)) {
      if (command.toLowerCase().includes(key)) {
        return value;
      }
    }

    return null;
  }

  private parseAnimationType(command: string): string | null {
    // For different chart types
    if (this.hasKeywords(command, ['wipe', 'sweep'])) {
      return 'wipe';
    }
    if (this.hasKeywords(command, ['grow', 'expand'])) {
      return 'grow';
    }
    if (this.hasKeywords(command, ['fade'])) {
      return 'fade';
    }
    if (this.hasKeywords(command, ['spring', 'bounce'])) {
      return 'spring';
    }
    if (this.hasKeywords(command, ['morph', 'transform'])) {
      return 'morph';
    }

    return null;
  }

  getSuggestions(component: Component): string[] {
    const suggestions: string[] = [];

    if (component.properties?.animation !== false) {
      suggestions.push(
        "Disable animation",
        "Set animation duration to 500ms",
        "Use slow animation",
        "Add bounce easing"
      );
    } else {
      suggestions.push(
        "Enable animation",
        "Add fade in animation"
      );
    }

    suggestions.push(
      "Stagger animation by 50ms",
      "Loop animation 3 times",
      "Use ease-in-out easing",
      "Add hover animation"
    );

    return suggestions;
  }
}

export const animationMixin = new AnimationMixin();
